import { ChatUserRolesEnum } from 'constants/chats';
import { FullMessageType } from 'hooks/useWebsocketTypes';
import { SearchDialogUrlParams } from 'redux/utils/searchInDialog.utils';
import {
  ChatContentEnum,
  ChatDescription,
  ChatInterlocutor,
  ChatMessage,
  ChatUser,
  ChatUserStatus,
  Folder,
  SearchChatUser,
  SelectedDialogInfo,
  messageStatusEnum,
} from 'types/chatsType';
import { FileDescriptor } from 'types/fileTypes';
import {
  CreatePollPayload,
  GetPollAnswerUsersPayload,
  PollCreateResponse,
  PollInMessage,
  PollResultsDetails,
  PollResultsVoter,
  SubmitPollAnswerPayload,
} from 'types/pollTypes';
import { ActionType, PayloadErrorType } from 'types/reduxTypes';
import { ChatEditableMessage, ChatType } from 'types/stateTypes';
import * as constants from '../constants/chats';
import * as chatFoldersConstants from '../constants/chatFolders';

export type FetchChatsPayload = {
  folderId?: Folder['id'] | null;
  lastMessageId?: number | null;
  additionalFilters?: number;
  isAppend?: boolean;
};

export type FetchChatsSuccessPayload = {
  count: number;
  dialogs: ChatDescription[];
  isAppend?: boolean;
};

export type FetchLastChatPayload = {
  lastChatId: number;
  unreadMessages?: number;
};

export type GetChatInfoPayload = {
  selectedChatId: number;
  loadMore: boolean;
  unreadMessages?: number;
};

export type GetChatInfoSuccessPayload = {
  data: ChatMessage[];
  loadMore: boolean;
  id: number;
};

export type GetChatInfoFailedPayload = {
  message: string;
  loadMore: boolean;
  id: number;
};

export type GetUsersListPayload = {
  searchTextValue: string;
  isGroup?: boolean;
  isCreateGroup?: boolean;
  groupMembersIds?: string[];
};

export type GetUsersListSuccessPayload = {
  data: { users: SearchChatUser[] };
  userId?: string;
  isGroup?: boolean;
  isCreateGroup?: boolean;
  groupMembersIds?: string[];
};

export type CreateGroupPayload = {
  ids: (string | number)[];
  name: string;
  image?: { id?: string };
};

export type UpdateGroupPayload = {
  id: number;
  users: (string | number)[];
  name?: string;
  image?: { id?: string } | Record<string, never>;
};

export type UpdateGroupSuccessPayload = {
  id: number;
  name?: string;
  dialogImage?: FileDescriptor | string | null;
  image?: FileDescriptor | null;
  users: ChatUser[];
};

export type UpdateMessagePayload = {
  dialogId: number;
  messageId: number;
  updatedText: string;
  files?: FileDescriptor[] | null;
};

export type SetEditableMessagePayload = {
  mode?: boolean;
  payload?: (ChatEditableMessage & { files?: FileDescriptor[] | null }) | null;
};

export type ReadMessagesPayload = {
  id: number;
  messages: number[];
  status: messageStatusEnum;
};

export type ReadMessagesSuccessPayload = {
  dialogId: number;
  messages: number[];
  readStatus: messageStatusEnum;
};

export type ReceiveWebsocketMessagePayload = {
  fullMessage: FullMessageType;
  isUnread: boolean;
};

export type GetUpdatedChatInfoPayload = {
  id: number;
  messageInfo?: FullMessageType['action'];
  fullMessage?: FullMessageType;
};

export type GetUpdatedChatInfoSuccessPayload = SelectedDialogInfo & {
  fullMessage?: FullMessageType;
  isUnread?: boolean;
};

export type SearchChatsPayload = {
  category?: number;
  query: string;
  details?: number;
};

export type DeleteMessagesPayload = {
  messages: number[];
  forMe?: boolean;
};

export type UploadAttachedFilePayload = {
  formData: FormData;
  fileInfo?: { fileType: number }[];
};

export type UploadGroupAvatarPayload = {
  formData: FormData;
  fileInfo?: { fileType: number }[];
};

export type UploadFileSuccessPayload = {
  data: FileDescriptor | FileDescriptor[];
};

export type ChangeUploadFilesPayload = {
  fileId?: string;
  isClear: boolean;
};

export type AdminRolePayload = {
  dialogId: number;
  userId: string | number;
  role?: ChatUserRolesEnum;
};

export type CreateChatFolderPayload = {
  name: string;
  dialogIds: number[];
  dialogs?: ChatDescription[];
  ord?: number | null;
};

export type EditChatFolderPayload = CreateChatFolderPayload & {
  id: Folder['id'];
  dialogs: ChatDescription[];
};

export type PollVoteSuccessPayload = {
  poll: PollInMessage | null;
};

export type StopPollSuccessPayload = {
  poll: PollInMessage;
};

export type GetPollAnswerUsersSuccessPayload = {
  data: PollResultsVoter[];
  pollId: string;
  optionId: string;
  page: number;
  limit?: number;
};

export type DeleteDialogSuccessPayload = {
  id: number;
  isMobile: boolean;
};

export type CreateGroupSuccessPayload = {
  id: number;
};

export type OpenPendingChatPayload = {
  interlocutor: ChatInterlocutor;
};

export type InviteUserPayload = {
  id: number;
  user: string;
};

export type SearchInDialogSuccessPayload = {
  isAppend?: boolean;
  payload: ChatMessage[];
};

export type NavigateToChatMessagePayload = {
  dialogId: number;
  messageId: number;
};

export type UsersStatusSuccessItem = {
  userId: string;
  status: ChatUserStatus;
};

export type PinChatPayload = {
  dialogId: number;
  folderId?: number | null;
};

export function clearErrors(): ActionType<null> {
  return { payload: null, type: constants.CLEAR_ERRORS };
}

export function createPoll(payload: CreatePollPayload): ActionType<CreatePollPayload> {
  return { payload, type: constants.CREATE_POLL };
}
export function createPollSuccess(payload: PollCreateResponse): ActionType<PollCreateResponse> {
  return { payload, type: constants.CREATE_POLL_SUCCESS };
}
export function createPollFailed(payload: string): ActionType<string> {
  return { payload, type: constants.CREATE_POLL_FAILED };
}

export function submitPollAnswer(payload: SubmitPollAnswerPayload): ActionType<SubmitPollAnswerPayload> {
  return { payload, type: constants.SUBMIT_POLL_ANSWER };
}
export function submitPollAnswerSuccess(payload: PollVoteSuccessPayload): ActionType<PollVoteSuccessPayload> {
  return { payload, type: constants.SUBMIT_POLL_ANSWER_SUCCESS };
}
export function submitPollAnswerFailed(): ActionType<null> {
  return { payload: null, type: constants.SUBMIT_POLL_ANSWER_FAILED };
}

export function stopPoll(payload: { pollId: string }): ActionType<{ pollId: string }> {
  return { payload, type: constants.STOP_POLL };
}
export function stopPollSuccess(payload: StopPollSuccessPayload): ActionType<StopPollSuccessPayload> {
  return { payload, type: constants.STOP_POLL_SUCCESS };
}
export function stopPollFailed(): ActionType<null> {
  return { payload: null, type: constants.STOP_POLL_FAILED };
}

export function getPoll(payload: { pollId: string }): ActionType<{ pollId: string }> {
  return { payload, type: constants.GET_POLL };
}
export function getPollSuccess(payload: PollResultsDetails): ActionType<PollResultsDetails> {
  return { payload, type: constants.GET_POLL_SUCCESS };
}
export function getPollFailed(payload: string): ActionType<string> {
  return { payload, type: constants.GET_POLL_FAILED };
}

export function clearPollInfo(): ActionType<null> {
  return { payload: null, type: constants.CLEAR_POLL_INFO };
}
export function clearPollActionError(): ActionType<null> {
  return { payload: null, type: constants.CLEAR_POLL_ACTION_ERROR };
}

export function getPollAnswerUsers(payload: GetPollAnswerUsersPayload): ActionType<GetPollAnswerUsersPayload> {
  return { payload, type: constants.GET_POLL_ANSWER_USERS };
}
export function getPollAnswerUsersSuccess(
  payload: GetPollAnswerUsersSuccessPayload
): ActionType<GetPollAnswerUsersSuccessPayload> {
  return { payload, type: constants.GET_POLL_ANSWER_USERS_SUCCESS };
}
export function getPollAnswerUsersFailed(payload: string): ActionType<string> {
  return { payload, type: constants.GET_POLL_ANSWER_USERS_FAILED };
}

export function searchGlobalChat(payload: SearchChatsPayload): ActionType<SearchChatsPayload> {
  return { payload, type: constants.SEARCH_GLOBAL_CHAT };
}
export function searchGlobalChatSuccess(payload: ChatType['foundChats']): ActionType<ChatType['foundChats']> {
  return { payload, type: constants.SEARCH_GLOBAL_CHAT_SUCCESS };
}
export function searchGlobalChatFailed(payload: string): ActionType<string> {
  return { payload, type: constants.SEARCH_GLOBAL_CHAT_FAILED };
}

export function searchDialogs(payload: SearchChatsPayload): ActionType<SearchChatsPayload> {
  return { payload, type: constants.SEARCH_DIALOGS };
}
export function searchDialogsSuccess(payload: ChatDescription[]): ActionType<ChatDescription[]> {
  return { payload, type: constants.SEARCH_DIALOGS_SUCCESS };
}
export function searchDialogsFailed(payload: string): ActionType<string> {
  return { payload, type: constants.SEARCH_DIALOGS_FAILED };
}

export function getUsersList(payload: GetUsersListPayload): ActionType<GetUsersListPayload> {
  return { payload, type: constants.GET_USERS_LIST };
}
export function getUsersListSuccess(payload: GetUsersListSuccessPayload): ActionType<GetUsersListSuccessPayload> {
  return { payload, type: constants.GET_USERS_LIST_SUCCESS };
}
export function getUsersListFailed(payload: string): ActionType<string> {
  return { payload, type: constants.GET_USERS_LIST_FAILED };
}

export function fetchChats(payload?: FetchChatsPayload): ActionType<FetchChatsPayload | undefined> {
  return { payload, type: constants.FETCH_CHATS };
}
export function fetchChatsSuccess(payload: FetchChatsSuccessPayload): ActionType<FetchChatsSuccessPayload> {
  return { payload, type: constants.FETCH_CHATS_SUCCESS };
}
export function fetchChatsFailed(payload: string): ActionType<string> {
  return { payload, type: constants.FETCH_CHATS_FAILED };
}

export function fetchLastChat(payload: FetchLastChatPayload): ActionType<FetchLastChatPayload> {
  return { payload, type: constants.FETCH_LAST_CHAT };
}

export function openCreateGroupModal(): ActionType<null> {
  return { payload: null, type: constants.OPEN_CREATE_GROUP_MODAL };
}

export function getChatInfo(payload: GetChatInfoPayload): ActionType<GetChatInfoPayload> {
  return { payload, type: constants.GET_CHAT_INFO };
}
export function getChatInfoSuccess(payload: GetChatInfoSuccessPayload): ActionType<GetChatInfoSuccessPayload> {
  return { payload, type: constants.GET_CHAT_INFO_SUCCESS };
}
export function getChatInfoFailed(
  payload: GetChatInfoFailedPayload
): ActionType<GetChatInfoFailedPayload> & { message: string } {
  return { message: payload.message, payload, type: constants.GET_CHAT_INFO_FAILED };
}

export function selectChat(payload: number | null): ActionType<number | null> {
  return { payload, type: constants.SELECT_CHAT };
}
export function toggleChat(): ActionType<null> {
  return { payload: null, type: constants.TOGGLE_CHAT };
}

export function openPendingChat(interlocutor: ChatInterlocutor): ActionType<OpenPendingChatPayload> {
  return { payload: { interlocutor }, type: constants.OPEN_PENDING_CHAT };
}

export function openDialogWithUser(payload: string): ActionType<string> {
  return { payload, type: constants.OPEN_DIALOG_WITH_USER };
}

export function openExistingDialog(payload: number): ActionType<number> {
  return { payload, type: constants.OPEN_EXISTING_DIALOG };
}

export function getDialogInfoByPeer(): ActionType<null> {
  return { payload: null, type: constants.GET_DIALOG_INFO_BY_PEER };
}

export function getDialogInfoByPeerSuccess(payload: SelectedDialogInfo): ActionType<SelectedDialogInfo> {
  return { payload, type: constants.GET_DIALOG_INFO_BY_PEER_SUCCESS };
}

export function getDialogInfoByPeerFailed(payload: string): ActionType<string> {
  return { payload, type: constants.GET_DIALOG_INFO_BY_PEER_FAILED };
}

export function createGroup(payload: CreateGroupPayload): ActionType<CreateGroupPayload> {
  return { payload, type: constants.CREATE_GROUP };
}
export function createGroupSuccess(payload: CreateGroupSuccessPayload): ActionType<CreateGroupSuccessPayload> {
  return { payload, type: constants.CREATE_GROUP_SUCCESS };
}
export function createGroupFailed(payload: string): ActionType<string> {
  return { payload, type: constants.CREATE_GROUP_FAILED };
}

export function pinChat(payload: PinChatPayload): ActionType<PinChatPayload> {
  return { payload, type: constants.PIN_CHAT };
}
export function pinChatSuccess(payload: number): ActionType<number> {
  return { payload, type: constants.PIN_CHAT_SUCCESS };
}
export function pinChatFailed(payload: string): ActionType<string> {
  return { payload, type: constants.PIN_CHAT_FAILED };
}

export function unpinChat(payload: PinChatPayload): ActionType<PinChatPayload> {
  return { payload, type: constants.UNPIN_CHAT };
}
export function unpinChatSuccess(payload: number): ActionType<number> {
  return { payload, type: constants.UNPIN_CHAT_SUCCESS };
}
export function unpinChatFailed(payload: string): ActionType<string> {
  return { payload, type: constants.UNPIN_CHAT_FAILED };
}

export function enableNotifications(payload: number): ActionType<number> {
  return { payload, type: constants.ENABLE_NOTIFICATIONS };
}
export function enableNotificationsSuccess(payload: number): ActionType<number> {
  return { payload, type: constants.ENABLE_NOTIFICATIONS_SUCCESS };
}
export function enableNotificationsFailed(payload: string): ActionType<string> {
  return { payload, type: constants.ENABLE_NOTIFICATIONS_FAILED };
}

export function disableNotifications(payload: number): ActionType<number> {
  return { payload, type: constants.DISABLE_NOTIFICATIONS };
}
export function disableNotificationsSuccess(payload: number): ActionType<number> {
  return { payload, type: constants.DISABLE_NOTIFICATIONS_SUCCESS };
}
export function disableNotificationsFailed(payload: string): ActionType<string> {
  return { payload, type: constants.DISABLE_NOTIFICATIONS_FAILED };
}

export function increaseMessageCount(payload: ChatDescription[]): ActionType<ChatDescription[]> {
  return { payload, type: constants.INCREASE_MESSAGE_COUNT };
}
export function decreaseMessageCount(): ActionType<null> {
  return { payload: null, type: constants.DECREASE_MESSAGE_COUNT };
}

export function resetSearchResult(payload: number): ActionType<number> {
  return { payload, type: constants.RESET_SEARCH_RESULT };
}

export function postWebsocketMessage(): ActionType<null> {
  return { payload: null, type: constants.POST_WEBSOCKET_MESSAGE };
}
export function receiveWebsocketMessage(
  payload: ReceiveWebsocketMessagePayload
): ActionType<ReceiveWebsocketMessagePayload> {
  return { payload, type: constants.RECEIVE_WEBSOCKET_MESSAGE };
}

export function deleteChat(payload: number): ActionType<number> {
  return { payload, type: constants.DELETE_CHAT };
}
export function deleteChatSuccess(payload: DeleteDialogSuccessPayload): ActionType<DeleteDialogSuccessPayload> {
  return { payload, type: constants.DELETE_CHAT_SUCCESS };
}
export function deleteChatFailed(payload: string): ActionType<string> {
  return { payload, type: constants.DELETE_CHAT_FAILED };
}

export function deleteGroupChat(payload: number): ActionType<number> {
  return { payload, type: constants.DELETE_GROUP_CHAT };
}
export function deleteGroupChatSuccess(payload: DeleteDialogSuccessPayload): ActionType<DeleteDialogSuccessPayload> {
  return { payload, type: constants.DELETE_GROUP_CHAT_SUCCESS };
}
export function deleteGroupChatFailed(payload: string): ActionType<string> {
  return { payload, type: constants.DELETE_GROUP_CHAT_FAILED };
}

export function updateMessage(payload: UpdateMessagePayload): ActionType<UpdateMessagePayload> {
  return { payload, type: constants.UPDATE_MESSAGE };
}
export function updateMessageSuccess(payload: UpdateMessagePayload): ActionType<UpdateMessagePayload> {
  return { payload, type: constants.UPDATE_MESSAGE_SUCCESS };
}
export function updateMessageFailed(payload: string): ActionType<string> {
  return { payload, type: constants.UPDATE_MESSAGE_FAILED };
}

export function setEditableMessage(
  payload?: SetEditableMessagePayload
): ActionType<SetEditableMessagePayload | undefined> {
  return { payload, type: constants.SET_EDITABLE_MESSAGE };
}

export function deleteMessages(payload: DeleteMessagesPayload): ActionType<DeleteMessagesPayload> {
  return { payload, type: constants.DELETE_MESSAGES };
}
export function deleteMessagesSuccess(): ActionType<null> {
  return { payload: null, type: constants.DELETE_MESSAGES_SUCCESS };
}
export function deleteMessagesFailed(payload: string): ActionType<string> {
  return { payload, type: constants.DELETE_MESSAGES_FAILED };
}

export function setAnswerMessage(payload: ChatMessage | null): ActionType<ChatMessage | null> {
  return { payload, type: constants.SET_ANSWER_MESSAGE };
}
export function changeEditMessageMode(payload: boolean): ActionType<boolean> {
  return { payload, type: constants.CHANGE_EDIT_MESSAGE_MODE };
}

export function getDialogInfo(payload: number): ActionType<number> {
  return { payload, type: constants.GET_DIALOG_INFO };
}
export function getDialogInfoSuccess(payload: SelectedDialogInfo): ActionType<SelectedDialogInfo> {
  return { payload, type: constants.GET_DIALOG_INFO_SUCCESS };
}
export function getDialogInfoFailed(payload: string): ActionType<string> {
  return { payload, type: constants.GET_DIALOG_INFO_FAILED };
}

export function clearDialogInfo(): ActionType<null> {
  return { payload: null, type: constants.CLEAR_DIALOG_INFO };
}
export function clearChats(): ActionType<null> {
  return { payload: null, type: constants.CLEAR_CHATS };
}
export function clearPolls(): ActionType<null> {
  return { payload: null, type: constants.CLEAR_POLLS };
}

export function getUsersStatus(payload: string[]): ActionType<string[]> {
  return { payload, type: constants.GET_USERS_STATUS };
}
export function getUsersStatusSuccess(payload: UsersStatusSuccessItem[]): ActionType<UsersStatusSuccessItem[]> {
  return { payload, type: constants.GET_USERS_STATUS_SUCCESS };
}
export function getUsersStatusFailed(payload: string): ActionType<string> {
  return { payload, type: constants.GET_USERS_STATUS_FAILED };
}

export function addStatusToChats(): ActionType<null> {
  return { payload: null, type: constants.ADD_STATUS_TO_CHATS };
}

export function readMessages(payload: ReadMessagesPayload): ActionType<ReadMessagesPayload> {
  return { payload, type: constants.READ_MESSAGES };
}
export function readMessagesSuccess(payload: ReadMessagesSuccessPayload): ActionType<ReadMessagesSuccessPayload> {
  return { payload, type: constants.READ_MESSAGES_SUCCESS };
}
export function readMessagesFailed(payload: string): ActionType<string> {
  return { payload, type: constants.READ_MESSAGES_FAILED };
}

export function inviteUser(payload: InviteUserPayload): ActionType<InviteUserPayload> {
  return { payload, type: constants.INVITE_USER };
}
export function inviteUserSuccess(payload: number): ActionType<number> {
  return { payload, type: constants.INVITE_USER_SUCCESS };
}
export function inviteUserFailed(payload: string): ActionType<string> {
  return { payload, type: constants.INVITE_USER_FAILED };
}

export function uploadFileFailed(payload: string): ActionType<string> {
  return { payload, type: constants.UPLOAD_FILE_FAILED };
}
export function uploadAttachedFile(payload: UploadAttachedFilePayload): ActionType<UploadAttachedFilePayload> {
  return { payload, type: constants.UPLOAD_ATTACHED_FILE };
}
export function uploadAttachedFileSuccess(payload: UploadFileSuccessPayload): ActionType<UploadFileSuccessPayload> {
  return { payload, type: constants.UPLOAD_ATTACHED_FILE_SUCCESS };
}
export function uploadGroupAvatar(payload: UploadGroupAvatarPayload): ActionType<UploadGroupAvatarPayload> {
  return { payload, type: constants.UPLOAD_GROUP_AVATAR };
}
export function uploadGroupAvatarSuccess(payload: UploadFileSuccessPayload): ActionType<UploadFileSuccessPayload> {
  return { payload, type: constants.UPLOAD_GROUP_AVATAR_SUCCESS };
}

export function updateGroup(payload: UpdateGroupPayload): ActionType<UpdateGroupPayload> {
  return { payload, type: constants.UPDATE_GROUP };
}
export function updateGroupSuccess(payload: UpdateGroupSuccessPayload): ActionType<UpdateGroupSuccessPayload> {
  return { payload, type: constants.UPDATE_GROUP_SUCCESS };
}
export function updateGroupFailed(payload: PayloadErrorType): ActionType<PayloadErrorType> {
  return { payload, type: constants.UPDATE_GROUP_FAILED };
}

export function removeUploadGroupAvatar(): ActionType<null> {
  return { payload: null, type: constants.REMOVE_UPLOAD_GROUP_AVATAR };
}

export function leaveGroup(payload: number): ActionType<number> {
  return { payload, type: constants.LEAVE_GROUP };
}
export function leaveGroupSuccess(payload: DeleteDialogSuccessPayload): ActionType<DeleteDialogSuccessPayload> {
  return { payload, type: constants.LEAVE_GROUP_SUCCESS };
}
export function leaveGroupFailed(payload: string): ActionType<string> {
  return { payload, type: constants.LEAVE_GROUP_FAILED };
}

export function searchInDialog(payload: SearchDialogUrlParams): ActionType<SearchDialogUrlParams> {
  return { payload, type: constants.SEARCH_IN_DIALOG };
}
export function searchInDialogSuccess(payload: SearchInDialogSuccessPayload): ActionType<SearchInDialogSuccessPayload> {
  return { payload, type: constants.SEARCH_IN_DIALOG_SUCCESS };
}
export function searchInDialogFailed(payload: string): ActionType<string> {
  return { payload, type: constants.SEARCH_IN_DIALOG_FAILED };
}

export function clearSearchInDialog(): ActionType<null> {
  return { payload: null, type: constants.CLEAR_SEARCH_IN_DIALOG };
}
export function navigateToChatMessageRequest(
  payload: NavigateToChatMessagePayload
): ActionType<NavigateToChatMessagePayload> {
  return { payload, type: constants.NAVIGATE_TO_CHAT_MESSAGE_REQUEST };
}
export function changeUploadFiles(payload: ChangeUploadFilesPayload): ActionType<ChangeUploadFilesPayload> {
  return { payload, type: constants.CHANGE_UPLOAD_FILES };
}
export function clearNotificationInfo(): ActionType<null> {
  return { payload: null, type: constants.CLEAR_NOTIFICATION_INFO };
}

export function getUpdatedChatInfo(payload: GetUpdatedChatInfoPayload): ActionType<GetUpdatedChatInfoPayload> {
  return { payload, type: constants.GET_UPDATED_CHAT_INFO };
}
export function getUpdatedChatInfoSuccess(
  payload: GetUpdatedChatInfoSuccessPayload
): ActionType<GetUpdatedChatInfoSuccessPayload> {
  return { payload, type: constants.GET_UPDATED_CHAT_INFO_SUCCESS };
}
export function getUpdatedChatInfoFailed(payload: string): ActionType<string> {
  return { payload, type: constants.GET_UPDATED_CHAT_INFO_FAILED };
}

export function changeLoadMessageStatus(payload: boolean): ActionType<boolean> {
  return { payload, type: constants.CHANGE_LOAD_MESSAGE_STATUS };
}
export function setGroupUserList(payload: ChatType['groupUserList']): ActionType<ChatType['groupUserList']> {
  return { payload, type: constants.SET_GROUP_USER_LIST };
}
export function setFolderData(payload: ChatType['editFolderData']): ActionType<ChatType['editFolderData']> {
  return { payload, type: chatFoldersConstants.SET_FOLDER_DATA };
}

export function setSelectionMode(payload: boolean): ActionType<boolean> {
  return { payload, type: constants.SET_SELECTION_MODE };
}
export function setSelectedMessages(payload: number[]): ActionType<number[]> {
  return { payload, type: constants.SET_SELECTED_MESSAGES };
}

export function getFolderDialogs(payload: { id: Folder['id'] }): ActionType<{ id: Folder['id'] }> {
  return { payload, type: chatFoldersConstants.GET_FOLDER_DIALOGS };
}
export function getFolderDialogsFailed(payload: string): ActionType<string> {
  return { payload, type: chatFoldersConstants.GET_FOLDER_DIALOGS_FAILED };
}
export function getFolderDialogsSuccess(payload: ChatDescription[]): ActionType<ChatDescription[]> {
  return { payload, type: chatFoldersConstants.GET_FOLDER_DIALOGS_SUCCESS };
}

export function getChatFolders(): ActionType<null> {
  return { payload: null, type: chatFoldersConstants.GET_CHAT_FOLDERS };
}
export function getChatFoldersFailed(payload: string): ActionType<string> {
  return { payload, type: chatFoldersConstants.GET_CHAT_FOLDERS_FAILED };
}
export function getChatFoldersSuccess(payload: Folder[]): ActionType<Folder[]> {
  return { payload, type: chatFoldersConstants.GET_CHAT_FOLDERS_SUCCESS };
}

export function reorderFolders(payload: Folder[]): ActionType<Folder[]> {
  return { payload, type: chatFoldersConstants.REORDER_FOLDERS };
}
export function reorderFoldersFailed(payload: string): ActionType<string> {
  return { payload, type: chatFoldersConstants.REORDER_FOLDERS_FAILED };
}
export function reorderFoldersSuccess(): ActionType<null> {
  return { payload: null, type: chatFoldersConstants.REORDER_FOLDERS_SUCCESS };
}

export function setFolders(payload: Folder[]): ActionType<Folder[]> {
  return { payload, type: chatFoldersConstants.SET_FOLDERS };
}

export function createChatFolder(payload: CreateChatFolderPayload): ActionType<CreateChatFolderPayload> {
  return { payload, type: chatFoldersConstants.CREATE_CHAT_FOLDER };
}
export function createChatFolderFailed(payload: string): ActionType<string> {
  return { payload, type: chatFoldersConstants.CREATE_CHAT_FOLDER_FAILED };
}
export function createChatFolderSuccess(payload: Folder): ActionType<Folder> {
  return { payload, type: chatFoldersConstants.CREATE_CHAT_FOLDER_SUCCESS };
}

export function editChatFolder(payload: EditChatFolderPayload): ActionType<EditChatFolderPayload> {
  return { payload, type: chatFoldersConstants.EDIT_CHAT_FOLDER };
}
export function editChatFolderFailed(payload: string): ActionType<string> {
  return { payload, type: chatFoldersConstants.EDIT_CHAT_FOLDER_FAILED };
}
export function editChatFolderSuccess(payload: EditChatFolderPayload): ActionType<EditChatFolderPayload> {
  return { payload, type: chatFoldersConstants.EDIT_CHAT_FOLDER_SUCCESS };
}

export function deleteChatFolder(payload: Folder['id']): ActionType<Folder['id']> {
  return { payload, type: chatFoldersConstants.DELETE_CHAT_FOLDER };
}
export function deleteChatFolderFailed(payload: string): ActionType<string> {
  return { payload, type: chatFoldersConstants.DELETE_CHAT_FOLDER_FAILED };
}
export function deleteChatFolderSuccess(payload: Folder['id']): ActionType<Folder['id']> {
  return { payload, type: chatFoldersConstants.DELETE_CHAT_FOLDER_SUCCESS };
}

export function makeUserAdmin(payload: AdminRolePayload): ActionType<AdminRolePayload> {
  return { payload, type: constants.MAKE_USER_ADMIN };
}
export function makeUserAdminFailed(payload: string): ActionType<string> {
  return { payload, type: constants.MAKE_USER_ADMIN_FAILED };
}
export function makeUserAdminSuccess(payload: string): ActionType<string> {
  return { payload, type: constants.MAKE_USER_ADMIN_SUCCESS };
}

export function removeAdmin(payload: AdminRolePayload): ActionType<AdminRolePayload> {
  return { payload, type: constants.REMOVE_ADMIN };
}
export function removeAdminFailed(payload: string): ActionType<string> {
  return { payload, type: constants.REMOVE_ADMIN_FAILED };
}
export function removeAdminSuccess(payload: string): ActionType<string> {
  return { payload, type: constants.REMOVE_ADMIN_SUCCESS };
}

export function blockDialog(payload: number): ActionType<number> {
  return { payload, type: constants.BLOCK_DIALOG };
}
export function blockDialogFailed(payload: string): ActionType<string> {
  return { payload, type: constants.BLOCK_DIALOG_FAILED };
}
export function blockDialogSuccess(payload: number): ActionType<number> {
  return { payload, type: constants.BLOCK_DIALOG_SUCCESS };
}

export function unblockDialog(payload: number): ActionType<number> {
  return { payload, type: constants.UNBLOCK_DIALOG };
}
export function unblockDialogFailed(payload: string): ActionType<string> {
  return { payload, type: constants.UNBLOCK_DIALOG_FAILED };
}
export function unblockDialogSuccess(payload: number): ActionType<number> {
  return { payload, type: constants.UNBLOCK_DIALOG_SUCCESS };
}

export function selectChatFolder(payload: Folder['id'] | null): ActionType<Folder['id'] | null> {
  return { payload, type: chatFoldersConstants.SELECT_CHAT_FOLDER };
}
export function setChatContent(payload: ChatContentEnum): ActionType<ChatContentEnum> {
  return { payload, type: constants.SET_CHAT_CONTENT };
}
